import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { toast } from "sonner";
import {
  Image as ImageIcon,
  UploadSimple,
  MagicWand,
  Plus,
  Trash,
  FloppyDisk,
  CaretDown,
  PencilSimple,
  MapPinSimple,
} from "@phosphor-icons/react";
import { geocodeAddress } from "../../lib/geocode";
import LocationPicker from "../LocationPicker";
import MenuImagesEditor from "../MenuImagesEditor";
import { FOOD_CATEGORIES } from "../../lib/foodCategories";

const DAYS = [
  "monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday",
];

function emptyEntry() {
  return {
    address: "",
    lat: null,
    lng: null,
    starts_time: "11:00",
    ends_time: "14:00",
    event_name: "",
    menu_image_paths: [],
  };
}

function emptyDays() {
  return DAYS.reduce((acc, d) => ({ ...acc, [d]: [] }), {});
}

/**
 * Admin-only tool. Two paths:
 *   1. Upload a flyer image → server-side OCR → review & edit
 *   2. "Start from blank" → key in the week directly (when OCR doesn't help)
 *
 * Per-entry editor mirrors the owner ScheduleEditor (`/dashboard`): address
 * + Find button + draggable LocationPicker map + time pickers, so the admin
 * gets the exact same precision an owner has when editing their own week.
 *
 * Saves replace the entire weekly schedule for the picked truck.
 *
 * `refreshKey` (bumped by parent when an invite is sent) re-fetches the
 * `/admin/trucks` dropdown so newly invited owners appear without a page
 * reload.
 */
export default function ScheduleExtractor({ refreshKey = 0 }) {
  const fileInputRef = useRef(null);
  const [trucks, setTrucks] = useState([]);
  const [selectedTruck, setSelectedTruck] = useState(""); // "owner_id::truck_id"
  const [loadingTrucks, setLoadingTrucks] = useState(true);

  const [fileName, setFileName] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [extracting, setExtracting] = useState(false);

  const [days, setDays] = useState(emptyDays());
  const [rawText, setRawText] = useState("");
  const [showRaw, setShowRaw] = useState(false);
  const [nameGuess, setNameGuess] = useState("");
  const [hasResult, setHasResult] = useState(false);
  const [geocodingId, setGeocodingId] = useState(null); // `${day}-${idx}`
  // The `week_start` (Monday ISO) the loaded data belongs to. Defaults to
  // null = "save targets this week"; populated by `loadExistingForTruck`
  // when admin pulls an existing schedule, so any pin corrections save
  // back to the SAME week the events came from (not the current week).
  const [loadedWeekStart, setLoadedWeekStart] = useState(null);
  const [loadedWeekLabel, setLoadedWeekLabel] = useState("");
  // Truck-level metadata (food category, menu) — admin can update these
  // alongside the schedule. Mirrors what owners can edit on /dashboard.
  const [foodCategory, setFoodCategory] = useState("");
  const [menuText, setMenuText] = useState("");
  const [menuImagePaths, setMenuImagePaths] = useState([]);
  // Active/upcoming `db.trucks` daily one-off posts for this truck. The
  // editor shows these as a separate "Currently live" section so admin
  // can fix mis-placed pins without clobbering the weekly schedule.
  const [dailyPosts, setDailyPosts] = useState([]);

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoadingTrucks(true);
    (async () => {
      try {
        const { data } = await api.get("/admin/trucks");
        // Sort alphabetically by truck_name so the dropdown is easy
        // to scan AND so new trucks slot in correctly on the next
        // refresh. Case-insensitive, locale-aware. Real user
        // request May 2026: "the trucks should be arranged
        // alphabetically by truck name. Please fix that, and have
        // it new trucks be entered into the queue alphabetically
        // as well."
        const sorted = [...(data || [])].sort((a, b) =>
          (a.truck_name || "").localeCompare(b.truck_name || "", undefined, { sensitivity: "base" })
        );
        if (alive) setTrucks(sorted);
      } catch (e) {
        if (alive) toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load trucks");
      } finally {
        if (alive) setLoadingTrucks(false);
      }
    })();
    return () => { alive = false; };
  }, [refreshKey]);

  useEffect(() => {
    // Clean up any object URL we created for the file preview
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const onFilePick = async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    if (!/\.(png|jpe?g|webp|gif)$/i.test(file.name)) {
      toast.error("Please upload a PNG, JPG, or WEBP image");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image is too large (max 10MB)");
      return;
    }
    setFileName(file.name);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(URL.createObjectURL(file));
    setExtracting(true);
    setHasResult(false);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/schedule-extract", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Ensure every day exists on the state object even if backend omitted it
      const freshDays = { ...emptyDays(), ...(data.days || {}) };
      setDays(freshDays);
      setRawText(data.raw_text || "");
      setNameGuess(data.truck_name_guess || "");
      setHasResult(true);
      const entryCount = Object.values(freshDays).reduce((s, arr) => s + (arr?.length || 0), 0);
      toast.success(`Extracted ${entryCount} schedule entr${entryCount === 1 ? "y" : "ies"} — review and edit below`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't run OCR on that image");
    } finally {
      setExtracting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const updateEntry = (day, idx, patch) => {
    setDays((prev) => {
      const arr = [...(prev[day] || [])];
      arr[idx] = { ...arr[idx], ...patch };
      return { ...prev, [day]: arr };
    });
  };
  const addEntry = (day) => {
    setDays((prev) => ({ ...prev, [day]: [...(prev[day] || []), emptyEntry()] }));
  };
  const removeEntry = (day, idx) => {
    setDays((prev) => {
      const arr = [...(prev[day] || [])];
      arr.splice(idx, 1);
      return { ...prev, [day]: arr };
    });
  };

  const onGeocodeEntry = async (day, idx) => {
    const entry = (days[day] || [])[idx];
    if (!entry?.address) return;
    const id = `${day}-${idx}`;
    setGeocodingId(id);
    const r = await geocodeAddress(entry.address);
    setGeocodingId(null);
    if (!r) {
      toast.error("Couldn't find that address — drop the pin manually below");
      return;
    }
    updateEntry(day, idx, { lat: r.lat, lng: r.lng });
    if (r.approximate) toast.message("Pinned approximate location (zip-level match)");
    else toast.success("Location pinned");
  };

  const startBlank = () => {
    setDays(emptyDays());
    setRawText("");
    setNameGuess("");
    setHasResult(true);
    setFileName("");
    setLoadedWeekStart(null);
    setLoadedWeekLabel("");
    setFoodCategory("");
    setMenuText("");
    setMenuImagePaths([]);
    setDailyPosts([]);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl("");
    }
  };

  const loadExistingForTruck = async () => {
    if (!selectedTruck) {
      toast.error("Pick a truck first");
      return;
    }
    const [ownerId, truckId] = selectedTruck.split("::");
    setExtracting(true);
    try {
      const { data } = await api.get(`/admin/schedules/${ownerId}/${truckId}`);
      const freshDays = { ...emptyDays(), ...(data.days || {}) };
      setDays(freshDays);
      setRawText("");
      setNameGuess(data.truck_name || "");
      setHasResult(true);
      setFileName("");
      setLoadedWeekStart(data.week_start || null);
      setLoadedWeekLabel(data.week_label || "");
      setFoodCategory(data.food_category || "");
      setMenuText(data.menu_text || "");
      setMenuImagePaths(Array.isArray(data.menu_image_paths) ? data.menu_image_paths : []);
      setDailyPosts(Array.isArray(data.daily_posts) ? data.daily_posts : []);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl("");
      }
      const count = Object.values(freshDays).reduce((s, arr) => s + (arr?.length || 0), 0);
      if (count === 0) {
        toast.message("This truck has no schedule on file yet — start adding stops below");
      } else if (data.fell_back_to_recent_week) {
        toast.success(
          `Loaded ${count} stop${count === 1 ? "" : "s"} from ${data.week_label || "the most recent week"} — drag any pin to fix it`,
          { duration: 5000 },
        );
      } else {
        toast.success(`Loaded ${count} stop${count === 1 ? "" : "s"} for this week — drag any pin to fix it`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load this truck's schedule");
    } finally {
      setExtracting(false);
    }
  };

  // Reasonable fallback when admin wants to drop a pin without geocoding.
  // Lancaster, PA — the app's primary service area. Admin drags from here
  // to the actual spot.
  const PIN_FALLBACK = { lat: 40.0379, lng: -76.3055 };
  const dropPinManually = (day, idx) => {
    const entry = (days[day] || [])[idx];
    if (!entry) return;
    // Reuse the most-recently-pinned coords if present in any other entry,
    // so the second pin starts near the first instead of jumping back to
    // the city center.
    let seed = PIN_FALLBACK;
    outer: for (const d of DAYS) {
      for (const e of days[d] || []) {
        if (e.lat != null && e.lng != null) {
          seed = { lat: e.lat, lng: e.lng };
          break outer;
        }
      }
    }
    updateEntry(day, idx, { lat: seed.lat, lng: seed.lng });
    toast.message("Pin dropped — drag it to the exact spot");
  };

  const onApply = async () => {
    if (!selectedTruck) {
      toast.error("Pick a truck to apply the schedule to first");
      return;
    }
    const [owner_id, truck_id] = selectedTruck.split("::");
    const picked = trucks.find((t) => t.truck_id === truck_id && t.owner_id === owner_id);
    const truckLabel = picked ? `${picked.truck_name} (${picked.owner_email})` : "this truck";
    if (!window.confirm(
      `Replace the entire weekly schedule for ${truckLabel}?\n\n` +
      `This will overwrite everything currently on file.`,
    )) return;

    // Strip empty addresses client-side — the backend otherwise tries to
    // geocode blanks. An empty day just means "no entries".
    const cleanDays = {};
    for (const d of DAYS) {
      const arr = (days[d] || []).filter((e) => e && (e.address || "").trim() !== "");
      cleanDays[d] = arr;
    }

    setSaving(true);
    try {
      const payload = {
        days: cleanDays,
        food_category: foodCategory || "",
        menu_text: menuText || "",
        menu_image_paths: menuImagePaths || [],
      };
      // When admin loaded an existing schedule from a previous week,
      // pin corrections must save back to THAT week (otherwise we'd
      // overwrite this-week's empty schedule and leave the original
      // mis-pinned events untouched). Falls through to backend default
      // (this Monday) when there's no loaded week.
      if (loadedWeekStart) payload.week_start = loadedWeekStart;
      await api.put(`/admin/schedules/${owner_id}/${truck_id}`, payload);
      const weekText = loadedWeekLabel || (loadedWeekStart ? `week of ${loadedWeekStart}` : "this week");
      toast.success(`Schedule applied to ${truckLabel} (${weekText})`);
      // Keep the edits on screen so the admin can make another round if they
      // spot anything — but flag the successful apply.
    } catch (e) {
      toast.error(
        formatApiError(e.response?.data?.detail) ||
        "Couldn't save the schedule — please try again",
      );
    } finally {
      setSaving(false);
    }
  };

  const entryCount = Object.values(days).reduce((s, arr) => s + (arr?.length || 0), 0);

  return (
    <section data-testid="admin-schedule-extractor">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-3">
        <div>
          <h2 className="font-display font-semibold text-xl tracking-tight">
            Schedule image extractor
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Upload a flyer — we&apos;ll OCR it and pre-fill the week. Admin-only.
          </p>
        </div>
        {hasResult && (
          <span className="text-xs text-muted-foreground">
            {entryCount} entr{entryCount === 1 ? "y" : "ies"} extracted
          </span>
        )}
      </div>

      <div className="rounded-2xl border border-border bg-card p-5 space-y-5">
        {/* Step 1 — pick truck + image */}
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="extract-truck">Apply to truck</Label>
            <select
              id="extract-truck"
              value={selectedTruck}
              onChange={(e) => setSelectedTruck(e.target.value)}
              disabled={loadingTrucks}
              className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
              data-testid="extract-truck-select"
            >
              <option value="">
                {loadingTrucks ? "Loading trucks…" : "— Choose a truck —"}
              </option>
              {trucks.map((t) => (
                <option
                  key={`${t.owner_id}::${t.truck_id}`}
                  value={`${t.owner_id}::${t.truck_id}`}
                >
                  {t.pending_invite ? "⏳ PENDING — " : ""}
                  {t.truck_name} · {t.owner_email}
                </option>
              ))}
            </select>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={loadExistingForTruck}
              disabled={!selectedTruck || extracting}
              className="gap-1.5 mt-1 rounded-full"
              data-testid="extract-load-existing-btn"
            >
              <MapPinSimple weight="duotone" className="h-3.5 w-3.5" />
              Load this week&apos;s schedule
            </Button>
            <p className="text-[11px] text-muted-foreground leading-tight">
              Use this to drag-correct mis-placed pins on a truck that&apos;s already live.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="extract-file">Schedule image</Label>
            <div className="flex items-center gap-2">
              <input
                ref={fileInputRef}
                id="extract-file"
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                onChange={onFilePick}
                className="hidden"
                data-testid="extract-file-input"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={extracting}
                className="gap-2 rounded-full"
                data-testid="extract-upload-btn"
              >
                <UploadSimple weight="duotone" className="h-4 w-4" />
                {extracting ? "Extracting…" : "Upload image"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={startBlank}
                disabled={extracting}
                className="gap-2 rounded-full text-muted-foreground hover:text-foreground"
                data-testid="extract-start-blank-btn"
              >
                <PencilSimple weight="duotone" className="h-4 w-4" />
                Or start blank
              </Button>
              {fileName && (
                <span className="text-xs text-muted-foreground truncate max-w-[200px]">
                  {fileName}
                </span>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground">
              PNG / JPG / WEBP · up to 10MB
            </p>
          </div>
        </div>

        {previewUrl && (
          <div className="rounded-xl border border-border bg-muted/30 p-3 flex gap-3 items-start">
            <img
              src={previewUrl}
              alt="uploaded flyer preview"
              className="h-32 w-32 object-cover rounded-lg border border-border flex-none"
            />
            <div className="min-w-0 flex-1">
              <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-primary font-semibold">
                <MagicWand weight="fill" className="h-3.5 w-3.5" />
                OCR source
              </div>
              <p className="text-sm mt-1 font-medium truncate">{fileName}</p>
              {nameGuess && (
                <p className="text-xs text-muted-foreground mt-0.5">
                  Detected name: <span className="text-foreground">{nameGuess}</span>
                </p>
              )}
              <button
                type="button"
                onClick={() => setShowRaw((s) => !s)}
                className="mt-2 inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
                data-testid="extract-toggle-raw"
              >
                <CaretDown className={`h-3 w-3 transition-transform ${showRaw ? "rotate-180" : ""}`} />
                {showRaw ? "Hide" : "Show"} raw OCR text
              </button>
              {showRaw && (
                <pre className="mt-2 max-h-40 overflow-auto rounded-md bg-background border border-border p-2 text-[11px] whitespace-pre-wrap font-mono">
                  {rawText || "(empty)"}
                </pre>
              )}
            </div>
          </div>
        )}

        {/* Step 2 — editable day-by-day preview */}
        {hasResult && (
          <div className="space-y-3" data-testid="extract-preview">
            {loadedWeekStart && loadedWeekLabel && (
              <div
                className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-xs text-amber-900 dark:text-amber-200"
                data-testid="extract-loaded-week-banner"
              >
                <strong>Editing {loadedWeekLabel}</strong> · pin corrections save back to that week.
              </div>
            )}
            <div className="flex items-center gap-2">
              <ImageIcon weight="duotone" className="h-4 w-4 text-primary" />
              <h3 className="text-sm font-semibold">Review &amp; edit before applying</h3>
            </div>

            {/* Truck-level metadata: pin icon (food category) + menu */}
            <div className="grid gap-3 sm:grid-cols-2 rounded-xl border border-border bg-background p-3 sm:p-4">
              <div className="space-y-1.5">
                <Label htmlFor="extract-food-cat">Pin icon (food category)</Label>
                <select
                  id="extract-food-cat"
                  value={foodCategory}
                  onChange={(e) => setFoodCategory(e.target.value)}
                  className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                  data-testid="extract-food-category-select"
                >
                  <option value="">— No category —</option>
                  {FOOD_CATEGORIES.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.emoji} {c.label}
                    </option>
                  ))}
                </select>
                <p className="text-[11px] text-muted-foreground">
                  Determines the truck&apos;s pin icon on the customer map.
                </p>
              </div>
              <div className="space-y-1.5">
                <Label>Menu images</Label>
                <MenuImagesEditor value={menuImagePaths} onChange={setMenuImagePaths} />
                <p className="text-[11px] text-muted-foreground">
                  Up to 3 images. Customers see them on the truck profile.
                </p>
              </div>
            </div>

            {/* Active/upcoming daily one-off events for this truck */}
            {dailyPosts.length > 0 && (
              <DailyPostsSection
                posts={dailyPosts}
                onPatchLocal={(idx, patch) =>
                  setDailyPosts((prev) =>
                    prev.map((p, i) => (i === idx ? { ...p, ...patch } : p)),
                  )
                }
                onSavedRemote={(idx, updated) =>
                  setDailyPosts((prev) =>
                    prev.map((p, i) => (i === idx ? { ...p, ...updated } : p)),
                  )
                }
              />
            )}

            <div className="grid gap-3">
              {DAYS.map((day) => (
                <DayCard
                  key={day}
                  day={day}
                  entries={days[day] || []}
                  onUpdate={(idx, patch) => updateEntry(day, idx, patch)}
                  onAdd={() => addEntry(day)}
                  onRemove={(idx) => removeEntry(day, idx)}
                  onGeocode={onGeocodeEntry}
                  onDropPin={dropPinManually}
                  geocodingId={geocodingId}
                />
              ))}
            </div>

            <div className="pt-2 flex flex-wrap items-center justify-end gap-2">
              <Button
                type="button"
                onClick={onApply}
                disabled={!selectedTruck || saving}
                className="gap-2 bg-primary text-primary-foreground hover:bg-primary/90 rounded-full h-11 px-5"
                data-testid="extract-apply-btn"
              >
                <FloppyDisk weight="duotone" className="h-4 w-4" />
                {saving ? "Applying…" : "Apply to truck's schedule"}
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground text-right">
              This <span className="font-semibold">replaces</span> the truck&apos;s entire weekly schedule. Addresses are auto-geocoded on save.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}

function DailyPostsSection({ posts, onPatchLocal, onSavedRemote }) {
  return (
    <div
      className="rounded-xl border border-emerald-300 bg-emerald-50/60 dark:bg-emerald-950/20 p-3 sm:p-4"
      data-testid="extract-daily-posts"
    >
      <div className="flex items-center gap-2">
        <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
        <h3 className="text-sm font-semibold text-emerald-900 dark:text-emerald-200">
          Currently live (one-off posts)
        </h3>
      </div>
      <p className="text-[11px] text-emerald-900/70 dark:text-emerald-200/70 mt-0.5">
        These are events the owner posted via &quot;Today&apos;s spot&quot; — saved separately from the weekly schedule. Edits here update the live event in place.
      </p>
      <div className="mt-3 space-y-3">
        {posts.map((p, idx) => (
          <DailyPostRow
            key={p.id || idx}
            post={p}
            onPatchLocal={(patch) => onPatchLocal(idx, patch)}
            onSavedRemote={(updated) => onSavedRemote(idx, updated)}
          />
        ))}
      </div>
    </div>
  );
}

function DailyPostRow({ post, onPatchLocal, onSavedRemote }) {
  const [saving, setSaving] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const isPinned = post.lat != null && post.lng != null;

  const onGeocode = async () => {
    if (!post.address) return;
    setGeocoding(true);
    const r = await geocodeAddress(post.address);
    setGeocoding(false);
    if (!r) {
      toast.error("Couldn't find that address — drag the pin manually");
      return;
    }
    onPatchLocal({ lat: r.lat, lng: r.lng });
    if (r.approximate) toast.message("Pinned approximate location");
    else toast.success("Location pinned");
  };

  const onDropPin = () => {
    onPatchLocal({ lat: post.lat ?? 40.0379, lng: post.lng ?? -76.3055 });
    toast.message("Pin dropped — drag it to the exact spot");
  };

  const onSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/daily-trucks/${post.id}`, {
        address: post.address,
        lat: post.lat,
        lng: post.lng,
        starts_at: post.starts_at,
        ends_at: post.ends_at,
      });
      toast.success("Live event updated");
      if (data?.truck) onSavedRemote(data.truck);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  // For datetime-local inputs we need "YYYY-MM-DDTHH:mm". Strip seconds/tz.
  const toLocal = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const fromLocal = (s) => (s ? new Date(s).toISOString() : "");

  return (
    <div
      className="rounded-lg border border-border bg-card p-3 space-y-2"
      data-testid={`daily-post-${post.id}`}
    >
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2">
        <div className="flex gap-2">
          <Input
            value={post.address || ""}
            onChange={(e) =>
              onPatchLocal({ address: e.target.value, lat: null, lng: null })
            }
            placeholder="Address"
            className="text-sm"
            data-testid={`daily-${post.id}-address`}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!post.address || geocoding}
            onClick={onGeocode}
            className="gap-1 flex-none"
            data-testid={`daily-${post.id}-find`}
          >
            <MapPinSimple weight="duotone" className="h-4 w-4" />
            {geocoding ? "…" : isPinned ? "✓" : "Find"}
          </Button>
          {!isPinned && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onDropPin}
              className="gap-1 flex-none text-muted-foreground"
              data-testid={`daily-${post.id}-drop-pin`}
            >
              Drop pin
            </Button>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <Input
          type="datetime-local"
          value={toLocal(post.starts_at)}
          onChange={(e) => onPatchLocal({ starts_at: fromLocal(e.target.value) })}
          className="text-sm"
          data-testid={`daily-${post.id}-starts`}
        />
        <Input
          type="datetime-local"
          value={toLocal(post.ends_at)}
          onChange={(e) => onPatchLocal({ ends_at: fromLocal(e.target.value) })}
          className="text-sm"
          data-testid={`daily-${post.id}-ends`}
        />
      </div>
      {isPinned && (
        <div className="space-y-1">
          <p className="text-[11px] text-muted-foreground">
            Pin looks wrong? Drag it to the exact spot.
          </p>
          <LocationPicker
            lat={post.lat}
            lng={post.lng}
            onChange={({ lat, lng }) => onPatchLocal({ lat, lng })}
          />
        </div>
      )}
      <div className="flex justify-end">
        <Button
          type="button"
          size="sm"
          onClick={onSave}
          disabled={saving}
          className="gap-2 rounded-full"
          data-testid={`daily-${post.id}-save`}
        >
          <FloppyDisk weight="duotone" className="h-3.5 w-3.5" />
          {saving ? "Saving…" : "Save live event"}
        </Button>
      </div>
    </div>
  );
}


function DayCard({ day, entries, onUpdate, onAdd, onRemove, onGeocode, onDropPin, geocodingId }) {
  const label = day[0].toUpperCase() + day.slice(1);
  return (
    <div className="rounded-xl border border-border bg-background p-3 sm:p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-semibold capitalize">
          {label}
          <span className="ml-2 text-xs font-normal text-muted-foreground">
            {entries.length === 0
              ? "closed"
              : `${entries.length} stop${entries.length === 1 ? "" : "s"}`}
          </span>
        </div>
        <button
          type="button"
          onClick={onAdd}
          className="inline-flex items-center gap-1 text-[12px] text-primary hover:text-primary/80 font-medium"
          data-testid={`extract-add-${day}`}
        >
          <Plus className="h-3.5 w-3.5" />
          Add stop
        </button>
      </div>
      {entries.length === 0 ? (
        <div className="text-xs text-muted-foreground italic">No stops for {label}.</div>
      ) : (
        <div className="space-y-3">
          {entries.map((e, idx) => {
            const gid = `${day}-${idx}`;
            const isPinned = e.lat != null && e.lng != null;
            return (
              <div
                key={idx}
                className="rounded-lg border border-border bg-card p-3 space-y-2"
                data-testid={`extract-entry-${day}-${idx}`}
              >
                <Input
                  value={e.event_name || ""}
                  onChange={(ev) => onUpdate(idx, { event_name: ev.target.value })}
                  placeholder="Event name (optional) — e.g. Penn Square"
                  className="font-display font-semibold text-sm"
                  data-testid={`extract-${day}-${idx}-event-name`}
                />
                <div className="grid grid-cols-1 md:grid-cols-[1fr_220px_44px] gap-2 items-center">
                  <div className="flex gap-2">
                    <Input
                      value={e.address || ""}
                      onChange={(ev) =>
                        onUpdate(idx, {
                          address: ev.target.value,
                          lat: null,
                          lng: null,
                        })
                      }
                      placeholder="Address (e.g. 100 N Queen St, Lancaster PA)"
                      className="text-sm"
                      data-testid={`extract-${day}-${idx}-address`}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={!e.address || geocodingId === gid}
                      onClick={() => onGeocode(day, idx)}
                      className="gap-1 flex-none"
                      data-testid={`extract-${day}-${idx}-find`}
                    >
                      <MapPinSimple weight="duotone" className="h-4 w-4" />
                      {geocodingId === gid ? "…" : isPinned ? "✓" : "Find"}
                    </Button>
                    {!isPinned && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => onDropPin(day, idx)}
                        className="gap-1 flex-none text-muted-foreground hover:text-foreground"
                        title="Drop a pin manually and drag it to the spot"
                        data-testid={`extract-${day}-${idx}-drop-pin`}
                      >
                        Drop pin
                      </Button>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      type="time"
                      value={e.starts_time || ""}
                      onChange={(ev) => onUpdate(idx, { starts_time: ev.target.value })}
                      className="text-sm"
                      data-testid={`extract-${day}-${idx}-starts`}
                    />
                    <span className="text-muted-foreground text-xs">to</span>
                    <Input
                      type="time"
                      value={e.ends_time || ""}
                      onChange={(ev) => onUpdate(idx, { ends_time: ev.target.value })}
                      className="text-sm"
                      data-testid={`extract-${day}-${idx}-ends`}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => onRemove(idx)}
                    className="h-9 w-9 grid place-items-center rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                    aria-label={`Remove ${label} stop ${idx + 1}`}
                    data-testid={`extract-${day}-${idx}-remove`}
                  >
                    <Trash className="h-4 w-4" />
                  </button>
                </div>

                {isPinned && (
                  <div className="space-y-1">
                    <p className="text-[11px] text-muted-foreground">
                      Pin looks wrong? Drag it to the exact spot.
                    </p>
                    <LocationPicker
                      lat={e.lat}
                      lng={e.lng}
                      onChange={({ lat, lng }) => onUpdate(idx, { lat, lng })}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
